// Case studies + Work index intro. Metric outcome numbers are intentionally
// marked TODO — do not invent them.

export const WORK_INTRO =
  "Six projects, written up properly. What was broken before, what we built, which parts were genuinely difficult, and what changed once it went live. Where we would do something differently a second time, we say so.";

export const WORK = {
  "delivery-logistics-platform": {
    slug: "delivery-logistics-platform",
    path: "/work/delivery-logistics-platform",
    navLabel: "Delivery & logistics platform",
    eyebrow: "Case study",
    h1: "Delivery and logistics platform",
    status: "Logistics · Oman · Ready for launch",
    statusKind: "building",
    thumbnailId: "cs-delivery-hero",
    meta: { title: "Case study — Delivery & logistics platform in Oman", description: "Four applications on one backend — customer app, driver app, admin console and shipper portal — built for launch in Oman." },
    intro: "Four applications on a single backend plus a public API — customer app, driver app, admin dashboard and a B2B shipper portal.",
    sections: [
      { type: "block", heading: "The problem we set out to solve", paragraphs: [
        "Delivery in Oman still runs on phone calls and paper — drivers assigned by WhatsApp, customers calling to ask where their parcel is, proof of delivery on a slip that gets lost, business clients with different agreed rates living in someone's memory.",
      ] },
      { type: "grid", heading: "What we're building", items: [
        { title: "Customer app", desc: "Multi-service booking, wallet, tracking." },
        { title: "Driver app", desc: "Offers, GPS, three-step POD." },
        { title: "Admin dashboard", desc: "Dispatch, agents, accounts." },
        { title: "Shipper portal", desc: "B2B, per-client rate cards." },
        { title: "Public order API", desc: "Partner-facing." },
        { title: "Accounts export", desc: "Excel, per client and per period." },
      ] },
      { type: "block", heading: "The hard parts",
        bullets: [
          "Auto-dispatch — offers cascade to the nearest available driver with a timeout, escalating outward, falling back to manual assignment, with separate driver pools for specialised delivery types.",
          "Pricing — four methods, per-shipper rate cards with fallback, driver commission engine underneath.",
          "Proof of delivery — name, photo and signature all mandatory, photos compressed on-device so they upload on poor mobile data.",
          "Waybills — barcode and QR, A6 PDF, batch printing, white-labelled per shipper.",
        ],
      },
      { type: "stack", heading: "Stack", items: ["React Native / Expo", "FastAPI", "React", "MongoDB Atlas", "Google Places"] },
      { type: "block", heading: "What launch will change",
        bullets: [
          "Move dispatch off WhatsApp onto an auto-assign engine with cascade and fallback",
          "Give business clients a self-serve portal with their agreed rates baked in",
          "Give customers real-time tracking and predictable ETAs instead of phone calls",
        ],
      },
    ],
    related: [
      { tag: "Solutions", label: "On-demand delivery platform", to: "/solutions/on-demand-delivery-platform" },
      { tag: "Industries", label: "Logistics & transportation", to: "/industries/logistics-transportation-in-oman" },
      { tag: "Services", label: "Mobile app development", to: "/services/mobile-app-development-company-in-oman" },
    ],
  },
  "warehouse-management-system": {
    slug: "warehouse-management-system", path: "/work/warehouse-management-system", eyebrow: "Case study",
    h1: "Warehouse management system", status: "Warehousing · Asia & USA · Delivered", statusKind: "delivered",
    thumbnailId: "cs-wms",
    meta: { title: "Case study — Warehouse management", description: "Bin-level accuracy, mobile picking and nightly reconciliation, delivered for operators in Singapore, Malaysia and the USA." },
    intro: "Full-stack WMS delivered for third-party logistics operators in Singapore, Malaysia and the USA — receiving, put-away, picking, packing, dispatch and stock counts.",
    sections: [],
    related: [
      { tag: "Solutions", label: "Warehouse management", to: "/solutions/warehouse-management" },
      { tag: "Industries", label: "Manufacturing & distribution", to: "/industries/manufacturing-distribution-oman" },
      { tag: "Services", label: "Enterprise software", to: "/services/enterprise-software-development-company-in-oman" },
    ],
  },
  "logistics-erp": {
    slug: "logistics-erp", path: "/work/logistics-erp", eyebrow: "Case study",
    h1: "Logistics ERP", status: "Logistics ERP · Arizona, USA · Delivered", statusKind: "delivered",
    thumbnailId: "cs-erp",
    meta: { title: "Case study — Logistics ERP (Arizona, USA)", description: "Finance, operations and dispatch joined up for a US-based logistics operator." },
    intro: "Finance, operations and dispatch joined up for a US-based logistics operator, delivered from Oman with an on-shore counterpart team.",
    sections: [],
    related: [
      { tag: "Services", label: "ERP & HRMS", to: "/services/erp-software-development-company-in-oman" },
      { tag: "Services", label: "Enterprise software", to: "/services/enterprise-software-development-company-in-oman" },
      { tag: "Solutions", label: "Logistics & fleet software", to: "/solutions/logistics-fleet-software" },
    ],
  },
  "showroom-management-platform": {
    slug: "showroom-management-platform", path: "/work/showroom-management-platform", eyebrow: "Case study",
    h1: "Showroom management platform", status: "Automotive · Muscat · Live", statusKind: "live",
    thumbnailId: "cs-showroom",
    meta: { title: "Case study — Showroom management platform (Muscat)", description: "Vehicle inventory, sales, leads, documents and a public listing site. Live in Muscat." },
    intro: "Vehicle inventory including consignment, sales, leads, reservations, payments, documents, printable forms, reporting, and a public listing site.",
    sections: [],
    related: [
      { tag: "Solutions", label: "Showroom management", to: "/solutions/showroom-management" },
      { tag: "Industries", label: "Automotive", to: "/industries/automotive-in-oman" },
      { tag: "Services", label: "CRM development", to: "/services/crm-software-development-company-in-oman" },
    ],
  },
  "marketplace-commerce": {
    slug: "marketplace-commerce", path: "/work/marketplace-commerce", eyebrow: "Case study",
    h1: "Marketplace commerce", status: "E-commerce · In build", statusKind: "building",
    thumbnailId: "cs-marketplace",
    meta: { title: "Case study — Marketplace commerce", description: "Multi-vendor commerce with split orders, commissions and payouts." },
    intro: "A multi-vendor marketplace in build for the Oman and GCC market — vendor onboarding, catalogue, commissions, split orders and payouts, designed to run alongside our delivery platform.",
    sections: [
      { type: "block", heading: "What we're building", bullets: ["Vendor onboarding, catalogue, commission engine and split orders", "Payments in, payouts out — reconciled per contract"] },
    ],
    related: [
      { tag: "Solutions", label: "E-commerce marketplace", to: "/solutions/e-commerce-marketplace" },
      { tag: "Industries", label: "Retail & e-commerce", to: "/industries/retail-e-commerce-in-oman" },
      { tag: "Solutions", label: "Warehouse management", to: "/solutions/warehouse-management" },
    ],
  },
  // Twasal Awasal intentionally does not have a case study here — it links
  // out to its own site via REACT_APP_TWASAL_URL (see InBuildNow.jsx).
};

export const WORK_LIST = Object.values(WORK);
